<template>
    <admin-layout title="Vouchers">
        <div>
            <TabComponent
            :tabs="tabs"
            :button-items="true"
            >
                <template #buttons>
                    <ButtonComponent
                        class="text-gray-900"
                        secondary
                        exportBtn
                        @click="exportVoucher()"
                        :disabled="loader"
                    >
                        Export
                    </ButtonComponent>
                    <ButtonComponent
                    v-if="$hasPermission('create-vouchers')"
                    createBtn
                    @click="router.push(`/vouchers/create`)"> 
                        Create
                    </ButtonComponent>
                </template>
            </TabComponent>
        </div>
        <!-- Filter -->
        <div class="px-6 py-3">
            <FilterComponent
                :search="searchText"
                @fetchData="searchData"
                :text-filters="currentRoutePath !== '/vouchers/logs'"
                @update:filters="applyFilters"
                :custom-filters="activeTab !== 'activity_logs'"
            >
                <template #fields>
                    <div class="space-y-6">
                        <DatePicker
                            id="filterDate"
                            name="filterDate"
                            range
                            is24
                            isPresetRanges
                            v-model="filterDate"
                            placeholder="Start Date & End Date"
                            label="Start Date & End Date"
                        />
                    </div>
                </template>
            </FilterComponent>
        </div>
        <div class="" v-if="currentRoutePath !== '/vouchers/logs'">
            <DataTable 
                :headers="headers" 
                :count="items?.data.length"
            >
                <template #body>
                    <tr v-for="(item, index) in items?.data" :key="index">
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ item.name }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ item.code }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900">
                            <p class="overflow-hidden line-clamp-1">{{ item.description ?? '--' }}</p>
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ item.max_usage_type == MaxUsageType.LIMITED ? `${item.usage_count ?? 0} / ${item.max_usage_count}` : item.usage_count ?? 0 }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            <template v-if="item.validity_range_type == ValidityRangeType.ANYTIME">
                                    Anytime
                            </template>
                            <template v-else>
                                {{ formatDate(item.start_at, 'ASIA/MANILA') }} - {{ formatDate(item.end_at, 'ASIA/MANILA') }}
                            </template>
                        </td>
                        <td class="px-6 py-5 text-sm whitespace-nowrap" :class="`text-${getStatus(item.status).color}-600`">
                            <StatusPill
                                :color="getStatus(item.status)?.color"
                                :text="getStatus(item.status)?.text"
                            />
                        </td>
                        <td class="px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                            
                            {{ `${formatDateTime(item.created_at, 'ASIA/MANILA')}` }}
                        </td>
                        <td class="text-center">
                            <ActionButton
                                edit-btn
                                @click="router.push(`/vouchers/show/${item.id}`)"
                            />
                        </td>
                    </tr>
                </template>
            </DataTable>
            
            <page-pagination class="mb-6" 
                :items="items"  
                @nextPage="indexVoucher"
            />
        </div>
        <div v-else>
            <ActivityLogsTable
                :logs="logs"
            />
        </div>
        <PageLoader v-if="loader" />

        <ErrorModal
            width="w-[548px]"
            title="Error"
            action-text="Close"
            cancel-text="Go back"
            :show="showError"
            @close="showError = false"
            @confirm="showError = false"
        >
            <template #content>
                {{ errorMsg }}
            </template>
        </ErrorModal>

        <SuccessModal
            :show="showSuccess"
            @cancel="showSuccess = false"
            @confirm="showSuccess = false"
            action-text="Close"
            title="Vouchers Export"
            content="Export is being processed. Please check your registered email."
        />

    </admin-layout>
</template>

<script setup lang="ts">
import AdminLayout from '@/layouts/AdminLayout.vue';
import FilterComponent from '@/components/FilterComponent.vue';
import DataTable from '@/components/DataTable.vue';
import ActionButton from '@/components/ActionButton.vue';
import TabComponent from '@/components/TabComponent.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import PageLoader from "@/components/PageLoader.vue";
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/modal/SuccessModal.vue';
import StatusPill from '@/components/StatusPill.vue';
import PagePagination from '@/components/PagePagination.vue';
import DatePicker from '@/components/inputs/DatePicker.vue';
import ActivityLogsTable from '@/components/ActivityLogsTable.vue';

import { useRouter } from 'vue-router';
import { ref, onMounted, computed, watch } from 'vue';
import throttle from "lodash/throttle";
import voucher from "@/classes/Voucher";
import { paginatePage } from '@/helpers/PageHelper';
import { MaxUsageType } from '@/enums/MaxUsageType';
import { ValidityRangeType } from '@/enums/ValidityRangeType';

import { formatDateTime, formatDate } from '@/helpers/TimeFormatter'

/**
 * ********************************
 * Data 
 * ********************************
 */
 const router           = useRouter();
 const loader           = ref(false);
 const showError        = ref(false);
 const errorMsg         = ref(null);
 const showSuccess      = ref(false);
//  const illnessId        = ref(route.params.id)
 const mainRoute        = ref(`/vouchers`);
 const currentRoutePath = computed(() => router.currentRoute.value.path);


const getStatus = (status: number) => {
    let color = 'yellow';
    let text = 'Unknown';

    switch (status) {
        case 1:
            color = 'violet';
            text = 'Scheduled';
            break;
        case 2:
            color = 'yellow';
            text = 'Ongoing';
            break;
        case 3:
            color = 'red';
            text = 'Expired';
            break;
        case 4:
            color = 'red';
            text = 'Cancelled';
            break;
    }

    return { color, text };
}

/**
 * ********************************
 * Page Renderer Data 
 * ********************************
 */
 const items           = ref(null);
const filterDate       = ref(null);
const tabCount    = ref(0);
const log_counts      = ref(0);
const logs            = ref({});


/**
 * ********************************
 * query 
 * ********************************
 */
const searchText = ref('');
const activeTab  = ref();
const goToPage   = ref();
const tab   = ref('all');

// Tabs
const tabs: { name: string, count: number, allowed: boolean }[] = computed(() => [
    {
        name: 'All',
        count: tabCount.value,
        href: mainRoute.value,
        allowed: true
    },
    {
        name: 'Scheduled',
        count: tabCount.value,
        href: `${mainRoute.value}/scheduled`,
        allowed: true
    },
    {
        name: 'Ongoing',
        count: tabCount.value,
        href: `${mainRoute.value}/ongoing`,
        allowed: true
    },
    {
        name: 'Expired',
        count: tabCount.value,
        href: `${mainRoute.value}/expired`,
        allowed: true
    },
    {
        name: 'Cancelled',
        count: tabCount.value,
        href: `${mainRoute.value}/cancelled`,
        allowed: true
    },
    {
        name: 'Activity Logs',
        count: log_counts.value,
        href: `${mainRoute.value}/logs`,
        allowed:
            window.$hasPermission('view-activity-logs')
    }
]);

// Headers
const headers: { text: string }[] = [
    { text: 'VOUCHER NAME' },
    { text: 'VOUCHER CODE' },
    { text: 'INFORMATION' },
    { text: 'USAGES' },
    { text: 'VALIDITY RANGE' },
    { text: 'STATUS'},
    { text: 'CREATED AT'}
];

/**
 * ********************************
 * Methods
 * ********************************
 */

const indexVoucher = async (data: string) => {
    try {
        loader.value = true;
        const queryString = searchText.value ? `&query=${searchText.value}` : 'query';
        const dateQuery = filterDate.value ? `&filterDate=${filterDate.value}` : '';
        const goToPageValue = paginatePage(data);
        let tabValue = tab.value;
        
        switch (currentRoutePath.value) {
            case '/vouchers/all':
                tabValue = 'all';
                break;
            case '/vouchers/scheduled':
                tabValue = 'scheduled';
                break;
            case '/vouchers/ongoing':
                tabValue = 'ongoing';
                break;
            case '/vouchers/expired':
                tabValue = 'expired';
                break;
            case '/vouchers/cancelled':
                tabValue = 'cancelled';
                break;
            case '/vouchers/logs':
                tabValue = 'all';
                break;
            default:
                tabValue = 'all'; // Default fallback
        }
        
        const response = await voucher.indexVoucher(`${goToPageValue}&tab=${tabValue}&${queryString}&${dateQuery}`)
        
        items.value = response?.data.items;
        logs.value = response?.data.logs;
        // ... handle other response data ...
    }
    catch(error) {
        showError.value = true;
        errorMsg.value = error.response.data.message;
            
        if(error?.response?.status == 401) {
            window.location.replace('/login');
        }
    }
    finally {
        loader.value = false;
    }
}

const exportVoucher = async () => {
    try {
        loader.value = true;
        const params = currentRoutePath.value === '/vouchers' ? '' : '';

        await voucher.exportVoucher(params);
        showSuccess.value = true;
    } catch (error: any) {
        showError.value = true;
        errorMsg.value  = error.response?.data.message;
    } finally {
        loader.value = false;
    }
};

const applyFilters = () => {
    indexVoucher(goToPage.value);
}


const searchData = (val: string) => {
    searchText.value = val
}

watch(
    searchText,
    throttle(() => {
        indexVoucher(goToPage.value);
    }, 1000)
);

watch(
    filterDate,
    () => {
        indexVoucher(goToPage.value);
    }
);

onMounted(() => {
    indexVoucher(goToPage.value);
})

</script>